import { useContext, useRef } from 'react'

import { get } from 'lodash'
import { KeyboardTimePicker } from '@material-ui/pickers'
import { makeStyles } from '@material-ui/core/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClock } from '@fortawesome/pro-light-svg-icons'

import ContentModalContext from '../../../ContentModalProvider/Context'
import ValidationMessage from '../../../components/ValidationMessage'
import { ERROR_MESSAGES } from '../../../components/constants'
import { useOnUpdateTime } from './use-on-update'
import { getDateObjFromTimeString, isValidDate, useDate } from './helpers'

const useStyles = makeStyles({
  picker: {
    margin: 0,
    width: '100%',
  },
  adornment: {
    textTransform: 'uppercase',
    marginRight: '-9px',
    marginLeft: 0,
  },
})

interface ITimeSelectorProps {
  fieldName: string
  label: string
  min: Date | null
  max: Date | null
}

const TimeSelector = ({ fieldName, label, min, max }: ITimeSelectorProps) => {
  const classes = useStyles()
  const { item, isEditing, validation } = useContext(ContentModalContext)
  const onUpdateTime = useOnUpdateTime(fieldName)

  const time = useDate(item, fieldName)
  const touchedRef = useRef(false)
  const touched = touchedRef.current || isEditing
  const id = `${fieldName}-time-selector`
  const limitError = min ? ERROR_MESSAGES.TOO_EARLY : ERROR_MESSAGES.TOO_LATE

  return (
    <>
      <KeyboardTimePicker
        required
        fullWidth
        mask="__:__ _M"
        margin="normal"
        id={id}
        label={label}
        value={time}
        variant="inline"
        inputVariant="outlined"
        keyboardIcon={
          <FontAwesomeIcon
            icon={faClock}
            size="xs"
          />
        }
        error={touched && validation.getHasError(label)}
        onChange={(time: Date | null) => {
          if (isValidDate(time)) {
            touchedRef.current = true
            onUpdateTime(time)
          }
        }}
        className={classes.picker}
        inputProps={{
          placeholder: '00:00 AM',
          onChange: (e) => {
            const v = get(e, 'target.value', '')
            const date = getDateObjFromTimeString(v)
            if (date) {
              touchedRef.current = true
              onUpdateTime(date)
            }
          },
        }}
        KeyboardButtonProps={{
          'aria-label': 'change time',
        }}
        InputLabelProps={{ shrink: true }}
        InputAdornmentProps={{
          className: classes.adornment,
          style: { display: 'none' },
        }}
      />
      <ValidationMessage
        id={id}
        label={label}
        touched={touched}
        valuesByError={{
          [limitError]: [min || max, time],
          [ERROR_MESSAGES.REQUIRED]: time,
          [ERROR_MESSAGES.VALID_TIME]: time,
        }}
      />
    </>
  )
}

export default TimeSelector
